import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { hasPermission } from '@/composables/usePermission'

export default function usePendingConfirmationsList() {
  const { role = null, worker_id = null, client = null } = store.state?.userStore?.userData
  const clientId = role !== 'admin' ? client?.$oid : null
  const { showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, deleteItem } = useLambda()
  const { pendingConfirmationsQueryWithOffset } = useMongoDBFunctions()
  const refPendingConfirmationsListTable = ref(null)
  const pendingConfirmations = ref([])
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
      { key: 'date', label: i18n.t('message.tableHeader.date') },
      { key: 'workerName.name', label: i18n.t('message.tableHeader.worker'), sortable: true },
      { key: 'processName.name', label: i18n.t('message.tableHeader.process'), sortable: true },
    ]
  })

  const totalPendingConfirmations = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('')
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const index = async () => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        search: searchQuery.value,
        sortBy: sortBy.value || 'date',
        sortOrder: isSortDirDesc.value ? -1 : 1,
        worker_id: (role !== 'admin') ? worker_id.$oid : null,
      }

      const { pendingConfirmations: p, totalRecords: total } = await pendingConfirmationsQueryWithOffset(input)
    
      p.forEach(e => {
        if (e.date) e.date = new Date(e.date).toLocaleDateString(`${default_language || 'en'}-US`)
      })

      pendingConfirmations.value = p
      if (currentPage.value === 1) totalPendingConfirmations.value = total
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_pending_confirmations') })
    } finally {
      isLoading.value = false
    }
  }

  const deletePendingConfirmation = async (id, callback) => {
    if (!hasPermission('pending-confirmations', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      callback(false)
      return
    }

    try {
      const query = { _id: oid(id) }
      if (clientId) query.client_id = oid(clientId)

      await deleteItem({ collection: 'confirmation', query })

      callback(true)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.pending_confirmation_remove_error'))
      callback(false)
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (pendingConfirmations.value.length ? 1 : 0)
    const to = pendingConfirmations.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalPendingConfirmations.value })
  })

  watch([currentPage, perPage], () => {
    index()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    index()
  })

  return {
    index,
    tableColumns,
    perPage,
    currentPage,
    totalPendingConfirmations,
    showingMessage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    pendingConfirmations,
    refPendingConfirmationsListTable,
    deletePendingConfirmation,
    isLoading,
  }
}
